import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import {
  Text,
  Stack,
  Box,
  Columns,
  Column,
  useResponsiveValue,
  Spread
} from '@machobear/design-system'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import AreaTitle from '@/components/area-title'
import { Breadcrumbs } from '@/components/breadcrumbs'
import SearchBar from '@/components/search-bar'
import { colors, breakpoints, fontWeights } from '@/styles'
import { pageHeader, pageHeaderMobile, pageHeaderHeightVar, pageHeaderInner } from './styling.css'
const PageHeaderOutter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const BreadcrumbsWrapper = styled.div`
  margin: 0 50px;
  padding: 20px 0 0;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 25px;
  }
`

const HeaderTitle = styled.h1`
  font-size: 30px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 41px;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    display: none;
  }
`

const SearchInner = styled.div`
  width: ${props => (props.buttonComponent ? '52%' : '45%')};
  > div,
  > a,
  > button {
    white-space: nowrap;
  }
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    > a,
    > button {
      display: none;
    }
  }
`

const ListingTableHeader = ({
  height = 158,
  title,
  description = '',
  searchString,
  onSearch,
  buttonComponent,
  breadcrumbs,
  children
}) => {
  const { t } = useTranslation()
  const ButtonComponent = buttonComponent
  const location = useLocation()
  const pathName = location.pathname
  const isSettings = pathName.includes('manage')
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  const headerHeight = isSettings ? 200 : height
  const spacing = isSettings ? 'settings' : 'default'
  const headerClass = isMobile ? pageHeaderMobile : pageHeader

  return (
    <Box
      className={headerClass({ spacing })}
      style={assignInlineVars({ [pageHeaderHeightVar]: `${headerHeight}px` })}
      width="full"
      position="sticky"
      background="white"
    >
      {breadcrumbs && (
        <BreadcrumbsWrapper>
          <Breadcrumbs items={breadcrumbs} />
        </BreadcrumbsWrapper>
      )}
      <PageHeaderOutter>
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          className={pageHeaderInner({ spacing, maxWidth: isSettings ? 'custom' : 'default' })}
        >
          {isSettings ? (
            <Stack space="xxsmall">
              {!isMobile && (
                <Box>
                  <AreaTitle textAlign="left" top="40" bottom="10">
                    {title}
                  </AreaTitle>

                  <Text tone="secondary" size="small">
                    {description}
                  </Text>
                </Box>
              )}

              <Spread
                space={{ mobile: 'none', tablet: 'gutter' }}
                direction={isMobile ? 'vertical' : 'horizontal'}
              >
                {children}
                <SearchInner>
                  {buttonComponent && <ButtonComponent />}
                  <SearchBar
                    placeholder={t('common.search', 'Search')}
                    searchString={searchString}
                    onSearch={onSearch}
                    fullWidth
                  />
                </SearchInner>
              </Spread>
            </Stack>
          ) : (
            <>
              <HeaderTitle>{title}</HeaderTitle>
              <Spread
                space={{ mobile: 'none', tablet: 'gutter' }}
                direction={isMobile ? 'vertical' : 'horizontal'}
              >
                {children}

                <SearchInner buttonComponent={buttonComponent !== undefined}>
                  {!isMobile && buttonComponent ? (
                    <Box height="touchable" display="flex" gap="gutter" width="full">
                      <ButtonComponent />
                      <SearchBar
                        placeholder={t('common.search', 'Search')}
                        searchString={searchString}
                        onSearch={onSearch}
                        fullWidth
                      />
                    </Box>
                  ) : (
                    <Columns space="small">
                      <Column width="5/5">
                        <SearchBar
                          placeholder={t('common.search', 'Search')}
                          searchString={searchString}
                          onSearch={onSearch}
                          fullWidth
                        />
                      </Column>
                    </Columns>
                  )}
                </SearchInner>
              </Spread>
            </>
          )}
        </Box>
      </PageHeaderOutter>
    </Box>
  )
}

export default ListingTableHeader
